import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";
import {
  NoDataContainer,
  pageSize,
  paginationLabel,
  TablePaginationStyle,
} from "../../Common/styles/table";
import { ExpandMore } from "@mui/icons-material";
import { errorToastMessage } from "../../../utils/toast";
import { AxiosResponse } from "axios";
import http from "../../../utils/http";
import { useParams } from "react-router-dom";
import { JournalAccordion } from "./FaqStyles";
import SearchIcon from "@mui/icons-material/Search";
import { debounce } from "lodash";

const FaqPreview = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<any>([]);
  const [dataCount, setDataCount] = useState<number>(0);
  const [expanded, setExpanded] = useState<string | false>(false);
  const [page, setPage] = useState(0);
  const { id: studyId, studySlug } = useParams();
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        let url = `/faq/slug/${studySlug}?page=${page + 1}&size=${pageSize}`;
        if (searchText) {
          url += `&search=${searchText}`;
        }
        const res: AxiosResponse = await http.get(url);
        const newData = res?.data?.data;
        setData(
          newData?.rows?.map((item: any) => {
            return {
              id: item?.id,
              question: item?.question,
              answer: item?.answer,
            };
          })
        );
        setDataCount(newData?.count);
        setLoading(false);
      } catch (err) {
        errorToastMessage(err as Error);
        setLoading(false);
      }
    };

    fetchData();
  }, [setLoading, setData, studyId, studySlug, page, searchText]);

  const handleChange =
    (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const modifySearchTerm = useMemo(
    () =>
      debounce((val) => {
        setPage(0);
        setSearchText(val);
      }, 500),
    [setPage, setSearchText]
  );

  return (
    <Box sx={{ p: 2.5, height: "100%", overflow: "auto" }}>
      <Typography variant="h6" mb={3}>
        Frequently Asked Questions
      </Typography>
      <Box
        sx={{
          bgcolor: "#FFFFFF",
          borderRadius: "8px 8px 0 0",
          mb: 2,
        }}
      >
        <TextField
          placeholder="Search for FAQs"
          style={{ width: "100%" }}
          sx={{
            input: {
              fontSize: "16px",
            },
          }}
          defaultValue={searchText}
          onChange={(e) => modifySearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Box>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <CircularProgress size={25} />
          </Box>
        ) : data.length > 0 ? (
          <Box>
            {data.map((item: any) => {
              return (
                <Accordion
                  key={item.id}
                  expanded={expanded === `panel${item.id}`}
                  onChange={handleChange(`panel${item.id}`)}
                  sx={JournalAccordion}
                >
                  <AccordionSummary
                    expandIcon={
                      <IconButton>
                        <ExpandMore sx={{ fontSize: 24 }} />
                      </IconButton>
                    }
                    sx={{
                      ".Mui-expanded": {
                        m: "12px 0px !important",
                      },
                    }}
                  >
                    <Box py={1.5}>
                      <Typography
                        fontWeight={600}
                        fontSize={"18px"}
                        color={"primary.main"}
                      >
                        {item.question}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: 2 }}>
                    <div className="tiptap-text" key={item.id}>
                      <div
                        dangerouslySetInnerHTML={{ __html: item?.answer }}
                      ></div>
                    </div>
                  </AccordionDetails>
                </Accordion>
              );
            })}
            {dataCount > pageSize && (
              <TablePagination
                sx={TablePaginationStyle}
                count={dataCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={pageSize}
                rowsPerPageOptions={[pageSize]}
                labelDisplayedRows={paginationLabel}
              />
            )}
          </Box>
        ) : (
          <NoDataContainer
            sx={{
              height: "100%",
              borderRadius: "12px",
              flexDirection: "column",
              gap: 4,
            }}
          >
            <Typography variant="subtitle1" fontWeight="medium" color="#4B5563">
              No FAQ Available
            </Typography>
          </NoDataContainer>
        )}
      </Box>
    </Box>
  );
};

export default FaqPreview;
